"use client"

import React, { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Menu, X, ChevronDown, Sparkles } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../../firebase"
import { useAuth } from "../../../contexts/AuthContext"
import "./header.css"
import Login from '../../login/login.jsx'
import EnquiryModal from '../../login/EnquiryModal.jsx'

const Header = () => {
  const authContext = useAuth()
  const user = authContext?.user || null
  const logout = authContext?.logout || (() => { })
  const [click, setClick] = useState(false)
  const [showProfileDropdown, setShowProfileDropdown] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [mobileSubMenu, setMobileSubMenu] = useState(null)
  const [mobileNestedMenu, setMobileNestedMenu] = useState(null)
  const [countries, setCountries] = useState([])
  const location = useLocation()
  const navRef = useRef(null)

  // Modal states
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [isSignupOpen, setIsSignupOpen] = useState(false)
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false)
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null)
        setShowProfileDropdown(false)
      }
    }

    const fetchCountries = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "countries"))
        const countriesList = querySnapshot.docs.map(doc => ({
          name: doc.data().name || doc.id.charAt(0).toUpperCase() + doc.id.slice(1).replace(/-/g, ' '),
          path: `/country/${doc.id}`
        }))
        setCountries(countriesList)
      } catch (error) {
        console.error("Error fetching countries:", error)
      }
    }

    window.addEventListener("scroll", handleScroll)
    document.addEventListener("mousedown", handleClickOutside)
    fetchCountries()
    return () => {
      window.removeEventListener("scroll", handleScroll)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Skills Programs",
      path: "/courses",
      dropdown: [
        {
          name: "Artificial Intelligence & Machine Learning",
          path: "/skills/ai",
          subDropdown: [
            { name: "Diploma in Artificial Intelligence", path: "/skills/ai" },
            { name: "Machine Learning Program", path: "/skills/machine" },
            { name: "Generative AI", path: "/skills/generative" },
            { name: "AI Foundations", path: "/skills/ai" },
          ],
        },
        {
          name: "Animation, VFX & Gaming",
          path: "/skills/animation",
          subDropdown: [
            { name: "3D Animation", path: "/skills/animation" },
            { name: "VFX & Compositing", path: "/skills/vfx" },
            { name: "Game Design", path: "/skills/game" },
            { name: "Motion Graphics", path: "/skills/motion" },
          ],
        },
        {
          name: "Digital Marketing & Content Creation",
          path: "/skills/marketing",
          subDropdown: [
            { name: "Digital Marketing Master", path: "/skills/marketing" },
            { name: "Social Media Marketing", path: "/skills/social" },
            { name: "Performance Marketing", path: "/skills/marketing" },
            { name: "Content Creation", path: "/skills/content" },
          ],
        },
        {
          name: "Data Science & Analytics",
          path: "/skills/data",
          subDropdown: [
            { name: "Data Science Program", path: "/skills/data" },
            { name: "Data Analytics", path: "/skills/analytics" },
            { name: "Python for Data Science", path: "/skills/python" },
            { name: "Business Analytics", path: "/skills/business" },
          ],
        },
        {
          name: "AR / VR Technology",
          path: "/skills/vr",
          subDropdown: [
            { name: "AR Development", path: "/skills/ar" },
            { name: "VR Experience Design", path: "/skills/vr" },
            { name: "Metaverse", path: "/skills/metaverse" },
          ],
        },
        {
          name: "Robotics & Automation",
          path: "/skills/robotics",
          subDropdown: [
            { name: "Robotics Engineering", path: "/skills/robotics" },
            { name: "Automation Systems", path: "/skills/automation" },
            { name: "IoT Systems", path: "/skills/iot" },
          ],
        },
      ],
    },
    {
      name: "Study Abroad",
      path: "/study-abroad",
      dropdown: countries.length > 0 ? countries : [
        { name: "UK", path: "/country/uk" },
        { name: "Canada", path: "/country/canada" },
        { name: "USA", path: "/country/usa" },
        { name: "Australia", path: "/country/australia" },
        { name: "Germany", path: "/country/germany" },
        { name: "Ireland", path: "/country/ireland" },
        { name: "France", path: "/country/france" },
        { name: "Dubai", path: "/country/dubai" },
      ],
    },
    {
      name: "Services",
      path: "/services",
      dropdown: [
        { name: "Visa Assistance", path: "/services/visa" },
        { name: "Accommodation", path: "/services/accommodation" },
        { name: "Career Counseling", path: "/services/career" },
        { name: "Test Preparation", path: "/services/test-prep" },
        { name: "Document Verification", path: "/services/documents" },
        { name: "Travel Insurance", path: "/services/insurance" },
        {
          name: "Degree Programs",
          path: "/degree-programs",
          subDropdown: [
            { name: "BCA + AI", path: "/degree-programs/bca" },
            { name: "BCA + Data Science", path: "/degree-programs/bca" },
            { name: "BBA + Marketing", path: "/degree-programs/bba" },
            { name: "BSc + Animation", path: "/degree-programs/bsc" },
          ],
        },
      ],
    },
    { name: "Events", path: "/events" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ]

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`header-nav ${isScrolled ? "scrolled" : ""}`}
      ref={navRef}
    >
      <div className="container">
        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img src="/logo.svg" alt="Welsh Education" className="nav-logo-img" />
        </Link>

        {/* Desktop Menu */}
        <ul className={click ? "nav-menu active" : "nav-menu"}>
          {navLinks.map((link) => (
            <li
              key={link.name}
              className={`nav-item ${link.dropdown ? "has-dropdown" : ""}`}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
              onMouseLeave={() => link.dropdown && setActiveDropdown(null)}
            >
              <Link
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                onClick={() => setClick(false)}
              >
                {link.name}
                {link.dropdown && <ChevronDown className="dropdown-icon" />}
              </Link>

              {link.dropdown && (
                <AnimatePresence>
                  {activeDropdown === link.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="dropdown-menu"
                    >
                      {link.dropdown.map((sub) => (
                        <div key={sub.name} className={`dropdown-item ${sub.subDropdown ? "has-sub-dropdown" : ""}`}>
                          <Link to={sub.path} onClick={() => setClick(false)}>
                            {sub.name}
                          </Link>
                          {sub.subDropdown && (
                            <div className="sub-dropdown-menu">
                              {sub.subDropdown.map((nested) => (
                                <Link key={nested.name} to={nested.path} onClick={() => setClick(false)}>
                                  {nested.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </li>
          ))}
        </ul>

        <div className="nav-actions">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="nav-cta-btn"
            onClick={() => setIsEnquiryOpen(true)}
          >
            Book Counseling
            <Sparkles className="cta-sparkle" />
          </motion.button>

          {user ? (
            <div className="nav-profile-container">
              <button
                className="nav-profile-btn"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                <span className="profile-avatar">
                  {(user.name || user.email || 'U')[0].toUpperCase()}
                </span>
              </button>

              <AnimatePresence>
                {showProfileDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="profile-dropdown-menu"
                  >
                    <div className="profile-user-info">
                      <span className="profile-name">{user.name || user.email?.split('@')[0] || "Student"}</span>
                      <span className="profile-email">{user.email}</span>
                    </div>
                    <div className="profile-dropdown-divider"></div>
                    <Link to="/profile?tab=courses" className="profile-dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                      My Courses
                    </Link>
                    <Link to="/profile?tab=edit" className="profile-dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                      Edit Profile
                    </Link>
                    <div className="profile-dropdown-divider"></div>
                    <button
                      className="profile-dropdown-item text-danger"
                      onClick={() => {
                        logout();
                        setShowProfileDropdown(false);
                      }}
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button
              className="nav-login-btn"
              onClick={() => setIsLoginOpen(true)}
            >
              Login
            </button>
          )}

          <button className="nav-toggle" onClick={() => setClick(!click)}>
            {click ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {click && (
            <motion.ul
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="nav-menu active"
            >
              {navLinks.map((link) => (
                <li key={link.name} className="mobile-nav-item">
                  <div className="mobile-nav-link-row">
                    <Link
                      to={link.path}
                      className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                      onClick={() => setClick(false)}
                    >
                      {link.name}
                    </Link>
                    {link.dropdown && (
                      <button
                        className={`mobile-dropdown-toggle ${mobileSubMenu === link.name ? "active" : ""}`}
                        onClick={() => setMobileSubMenu(mobileSubMenu === link.name ? null : link.name)}
                      >
                        <ChevronDown />
                      </button>
                    )}
                  </div>

                  {link.dropdown && mobileSubMenu === link.name && (
                    <div className="mobile-dropdown-content">
                      {link.dropdown.map((sub) => (
                        <div key={sub.name} className="mobile-dropdown-item">
                          <div className="mobile-nav-link-row">
                            <Link to={sub.path} onClick={() => setClick(false)}>
                              {sub.name}
                            </Link>
                            {sub.subDropdown && (
                              <button
                                className={`mobile-dropdown-toggle ${mobileNestedMenu === sub.name ? "active" : ""}`}
                                onClick={() => setMobileNestedMenu(mobileNestedMenu === sub.name ? null : sub.name)}
                              >
                                <ChevronDown />
                              </button>
                            )}
                          </div>
                          {sub.subDropdown && mobileNestedMenu === sub.name && (
                            <div className="mobile-nested-dropdown-content">
                              {sub.subDropdown.map((nested) => (
                                <Link
                                  key={nested.name}
                                  to={nested.path}
                                  className="mobile-nested-link"
                                  onClick={() => setClick(false)}
                                >
                                  {nested.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}

              {/* Mobile Auth Buttons */}
              <li className="mobile-nav-item mobile-auth-actions" style={{ display: 'flex', gap: '10px', padding: '20px 0', borderBottom: 'none' }}>
                {user ? (
                  <Link
                    to="/profile"
                    className="nav-auth-btn nav-profile-mobile"
                    style={{ flex: 1, justifyContent: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', textDecoration: 'none' }}
                    onClick={() => setClick(false)}
                  >
                    <span className="profile-avatar" style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                      {(user.name || user.email || 'U')[0].toUpperCase()}
                    </span>
                    Profile
                  </Link>
                ) : (
                  <button
                    className="nav-auth-btn nav-login"
                    style={{ flex: 1, justifyContent: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}
                    onClick={() => { setIsLoginOpen(true); setClick(false); }}
                  >
                    Login
                  </button>
                )}
              </li>
            </motion.ul>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <Login
        isLoginOpen={isLoginOpen}
        isSignupOpen={isSignupOpen}
        isForgotPasswordOpen={isForgotPasswordOpen}
        onCloseLogin={() => setIsLoginOpen(false)}
        onCloseSignup={() => setIsSignupOpen(false)}
        onCloseForgotPassword={() => setIsForgotPasswordOpen(false)}
        onOpenLogin={() => setIsLoginOpen(true)}
        onOpenSignup={() => setIsSignupOpen(true)}
        onOpenForgotPassword={() => setIsForgotPasswordOpen(true)}
      />
      <EnquiryModal
        isEnquiryOpen={isEnquiryOpen}
        onCloseEnquiry={() => setIsEnquiryOpen(false)}
      />
    </motion.nav>
  )
}

export default Header
